import { BeneficiaryInsightDimension } from '@repo/shared/dtos/beneficiary'
import { Button } from '@repo/ui/button'
import { Card, CardContent, CardHeader } from '@repo/ui/card'
import { CardTitle } from '@repo/ui/card'
import { FormField, FormMessage } from '@repo/ui/form'
import { useT } from '@repo/ui/hooks'
import { ArrowRightLeft } from 'lucide-react'
import { useSelector } from 'react-redux'

import { getCurrentOrganizationStructuredDataSchema } from '@/store/user'

import { AddDimensionButton } from './AddDimensionButton'
import { getAllowedStructuredDataTypes } from './helpers'
import RawDimensionForm from './RawDimensionForm'
import RawMetricForm from './RawMetricForm'
import { FormType } from './types'

export default function DimensionsForm({ form }: { form: FormType }) {
  const { t } = useT({ keyPrefix: 'admin.insights.form.dimensions' })
  const structuredDataSchema = useSelector(getCurrentOrganizationStructuredDataSchema)

  const isPieChart = form.watch('type') === 'pie'

  const firstDimension = form.watch('dimensions')[0]
  const secondDimension = form.watch('dimensions')[1]
  const hasTwoDimensions = firstDimension && secondDimension

  const switchDimensions = () => {
    if (!firstDimension || !secondDimension) return
    form.setValue('dimensions', [secondDimension, firstDimension])
    form.setValue('dimensionModes', [
      form.getValues('dimensionModes')[1]!,
      form.getValues('dimensionModes')[0]!,
    ])
  }

  const addDimension = (dimensionIndex: number, dimension: BeneficiaryInsightDimension) => {
    form.setValue(`dimensions.${dimensionIndex}`, dimension)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
      </CardHeader>
      <CardContent className="divide-y">
        <div className="space-y-2 pb-4">
          <h2>{t('yAxis.title')}</h2>
          <RawMetricForm form={form} />
        </div>

        <div className="space-y-2 pt-4">
          <div className="flex items-center space-x-2">
            <h2>{t('xAxis.title')}</h2>
            {hasTwoDimensions && (
              <Button variant="outline" size="xs" onClick={() => switchDimensions()}>
                <ArrowRightLeft className="w-4 h-4" />
              </Button>
            )}
          </div>

          <FormField
            control={form.control}
            name="dimensions"
            render={() => (
              <>
                <div className="space-x-2 flex divide-x w-full">
                  <div className="flex-1 pr-2 w-1/2">
                    {firstDimension ? (
                      <RawDimensionForm form={form} dimensionIndex={0} />
                    ) : (
                      <div className="flex justify-center mt-4">
                        <AddDimensionButton
                          schemaItems={getAllowedStructuredDataTypes(
                            form.getValues('dimensions'),
                            structuredDataSchema!,
                          )}
                          onAdd={(dimension) => addDimension(0, dimension)}
                        />
                      </div>
                    )}
                  </div>
                  {!isPieChart && (
                    <div className="flex-1 pl-4 w-1/2">
                      {secondDimension ? (
                        <RawDimensionForm form={form} dimensionIndex={1} />
                      ) : (
                        <div className="flex justify-center mt-4">
                          <AddDimensionButton
                            schemaItems={getAllowedStructuredDataTypes(
                              form.getValues('dimensions'),
                              structuredDataSchema!,
                            )}
                            onAdd={(dimension) => addDimension(1, dimension)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <FormMessage />
              </>
            )}
          />
        </div>
      </CardContent>
    </Card>
  )
}
